import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { HiArrowLeft, HiCheckCircle, HiExclamationCircle } from "react-icons/hi";

import { Button, cn } from "../../ui";
import AuthShell from "./components/AuthShell";
import OtpInput from "./components/OtpInput";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/AdminContext";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const VerifyOtp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAccount, resendOtp } = useAdminContext();

  const queryParams = new URLSearchParams(location.search);
  const email =
    (location.state as { email?: string } | null)?.email ??
    queryParams.get("email") ??
    "";

  const [otp, setOtp] = useState<string[]>(() => new Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(RESEND_SECONDS);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate("/signup", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setError(true);
      setSuccess(false);
      setMessage("Please enter the full 6-digit code.");
      return;
    }

    setLoading(true);
    setError(false);
    setMessage(null);

    try {
      const response = await verifyAccount({ email, code });
      if (response.status === 200) {
        setSuccess(true);
        setMessage("Verification successful. Redirecting…");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError(true);
        setMessage(response.message || "Invalid code. Please try again.");
      }
    } catch {
      setError(true);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    setMessage(null);
    setError(false);

    try {
      const response = await resendOtp({ email });
      if (response.status === 200) {
        setSuccess(true);
        setMessage("A new code has been sent to your email.");
        setTimeLeft(RESEND_SECONDS);
        setCanResend(false);
      } else {
        setError(true);
        setMessage(response.message || "Failed to resend code.");
      }
    } catch {
      setError(true);
      setMessage("Could not resend code.");
    } finally {
      setLoading(false);
    }
  };

  const code = otp.join("");

  return (
    <AuthShell eyebrow="Verify account">
      <Link
        to="/signup"
        className="mb-6 inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 transition-colors hover:text-gray-900"
      >
        <HiArrowLeft className="h-3 w-3" /> Back to signup
      </Link>

      <div className="mb-6 flex items-center justify-center lg:hidden">
        <img src={dosee_logo} alt="DoseeAfrica" className="h-10 w-auto" />
      </div>

      <header className="mb-8">
        <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-gray-900">
          Enter your code
        </h1>
        <p className="mt-2 text-[13.5px] text-gray-500">
          We sent a 6-digit code to{" "}
          <span className="font-medium text-gray-900">
            {email || "your email address"}
          </span>
          .
        </p>
      </header>

      <form onSubmit={handleVerify} className="space-y-6">
        <OtpInput
          value={otp}
          onChange={(next) => {
            setOtp(next);
            if (error) setError(false);
          }}
          error={error}
          disabled={loading}
        />

        {message ? (
          <div
            className={cn(
              "flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-[12.5px]",
              success
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-red-200 bg-red-50 text-red-700",
            )}
            role="alert"
          >
            {success ? (
              <HiCheckCircle className="mt-0.5 h-4 w-4 text-brand" />
            ) : (
              <HiExclamationCircle className="mt-0.5 h-4 w-4 text-red-600" />
            )}
            <span>{message}</span>
          </div>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={loading || code.length !== OTP_LENGTH}
        >
          {loading ? (
            <AiOutlineLoading className="h-4 w-4 animate-spin" />
          ) : (
            "Verify & continue"
          )}
        </Button>
      </form>

      <div className="mt-6 text-center text-[12px] text-gray-500">
        Didn’t receive a code?{" "}
        {canResend ? (
          <button
            type="button"
            onClick={handleResend}
            className="font-medium text-brand hover:text-[#007a7a]"
          >
            Resend code
          </button>
        ) : (
          <span className="text-gray-400">
            Resend in <span className="font-mono text-gray-600">{timeLeft}s</span>
          </span>
        )}
      </div>
    </AuthShell>
  );
};

export default VerifyOtp;
